import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Form,
  Label,
  Input,
  Spinner,
  Progress,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormFeedback,
} from "reactstrap";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { DebounceInput } from "react-debounce-input";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  getAds,
  getAd,
  deleteAd,
  toggleAdModalAction,
  uploadAdBanner,
  addAd,
  updateAd,
} from "../../store/ads/actions";
import ImageUploader from "../../Components/Common/ImageUploader.js";

const adTypes = [
  {
    id: "Intro",
    value: {
      en: "App Introduction Banner",
      ar: "لافتة مقدمة للتطبيق",
    },
    description: {
      en: "Will be displayed on Mobile App introduction page.",
      ar: "سيتم عرضه على صفحة مقدمة تطبيق الهاتف المحمول.",
    },
  },
  {
    id: "Customer_Home",
    value: {
      en: "Customer Home Banner",
      ar: "لافتة منزل العميل",
    },
    description: {
      en: "Will be displayed on Mobile App Customer Home Page.",
      ar: "سيتم عرضه على الصفحة الرئيسية لعميل تطبيق الهاتف المحمول.",
    },
  },
  {
    id: "Technician_Home",
    value: {
      en: "Technician Home Banner",
      ar: "فني الصفحة الرئيسية راية",
    },
    description: {
      en: "Will be displayed on Mobile App Technician Home Page.",
      ar: "سيتم عرضه على الصفحة الرئيسية لفني تطبيقات الهاتف المحمول.",
    },
  },
];

const AdsPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    // ads
    ads,
    loadingAds,
    ad,
    // add
    addAdLoading,
    addAdSuccess,
    // update
    updateAdLoading,
    updateAdSuccess,
    // banner upload
    uploadingAdBanner,
    uploadCount,
  } = useSelector((state) => state.Ads);

  document.title = `${t("Ads.Ads")} | ${t(process.env.REACT_APP_HEADING)}`;
  const [activeTab, setActiveTab] = useState(1);

  const [isAdModalOpen, setIsAdModalOpen] = useState(false);

  const toggleAdModal = () => {
    if (!isAdModalOpen) {
      dispatch(toggleAdModalAction());
      adForm.resetForm({
        title: "",
        arabicTitle: "",
        description: "",
        arabicDescription: "",
        type: "",
      });
      toggleTab(1, 0);
      setIsExistingImagesSet(false);
    } else {
      setBanners([]);
      setArabicBanners([]);
    }
    setIsAdModalOpen(!isAdModalOpen);
  };
  const [isAdDeleteModalOpen, setIsAdDeleteModalOpen] = useState(false);

  const toggleAdDeleteModal = () => {
    setIsAdDeleteModalOpen(!isAdDeleteModalOpen);
  };

  const [request, setRequest] = useState({
    limit: 10,
    page: 1,
    sortBy: "createdAt",
    sortDirection: "DESC",
  });

  const [totalRows, setTotalRows] = useState(0);

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">{t("Fields.Title")}</span>,
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            {i18n.language === "ar" ? row.arabicTitle : row.title}
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">
          {t("Fields.Description")}
        </span>
      ),
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            {i18n.language === "ar" ? row.arabicDescription : row.description}
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">{t("Fields.Type")}</span>,
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            {i18n.language === "ar"
              ? t("Common." + row.type)
              : t("Common." + row.type)}
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Created At")}</span>
      ),
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Action")}</span>
      ),
      sortable: false,
      cell: (row) => {
        return (
          <React.Fragment>
            <Button
              color="primary"
              className="btn-icon btn-sm"
              onClick={() => onEdit(row.id)}
            >
              <i className="ri-pencil-fill" />
            </Button>
            &nbsp;
            <Button
              color="danger"
              className="btn-icon btn-sm"
              onClick={() => onDelete(row.id)}
            >
              <i className="ri-delete-bin-5-line" />
            </Button>
          </React.Fragment>
        );
      },
    },
  ];

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let query = {
      limit: queryParams.has("limit") ? queryParams.get("limit") : 10,
      page: queryParams.has("page") ? queryParams.get("page") : 1,
      sortBy: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[0]
        : "createdAt",
      sortDirection: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[1]
        : "DESC",
      search: queryParams.has("search") ? queryParams.get("search") : "",
    };
    setDefaultSortAsc(query.sortDirection === "ASC");
    setDefaultSortFieldId(getSortByColumnId(query.sortBy));
    setRequest(query);
    dispatch(getAds(query));
  }, []);

  useEffect(() => {
    const { limit, page, search, sortBy, sortDirection } = request;
    let query;
    if (page) query = `?page=${page}`;
    if (limit) query += `&limit=${limit}`;
    if (search && search !== "") {
      history.replace({
        search: `&search=${search}`,
      });
    }
    if (sortBy && sortDirection) query += `&sortBy=${sortBy}:${sortDirection}`;
    if (search) query += `&search=${search}`;
    history.push({ search: query });
  }, [request]);

  useEffect(() => {
    setRows(ads?.data);
    setTotalRows(ads.meta?.totalItems);
  }, [ads, loadingAds]);

  const onChangeRowsPerPage = (pageSize) => {
    setRequest({ ...request, limit: pageSize });
    dispatch(getAds({ ...request, limit: pageSize }));
  };

  const onChangePage = (page) => {
    setRequest({ ...request, page: page });
    dispatch(getAds({ ...request, page: page }));
  };

  const onSearch = (text) => {
    setRequest({ ...request, search: text });
    dispatch(getAds({ ...request, search: text }));
  };

  useEffect(() => {
    if (addAdSuccess === true || updateAdSuccess === true) {
      toggleAdModal();
      dispatch(getAds(request));
    }
  }, [addAdSuccess, updateAdSuccess]);

  const getSortByColumnName = (id) => {
    let sortBy;
    if (i18n.language === "en") {
      switch (id) {
        case 1:
          sortBy = "title";
          break;
        case 2:
          sortBy = "description";
          break;
        case 3:
          sortBy = "type";
          break;
        case 4:
          sortBy = "createdAt";
          break;
        default:
          sortBy = "createdAt";
          break;
      }
    }

    if (i18n.language === "ar") {
      switch (id) {
        case 1:
          sortBy = "arabicTitle";
          break;
        case 2:
          sortBy = "arabicDescription";
          break;
        case 3:
          sortBy = "type";
          break;
        case 4:
          sortBy = "createdAt";
          break;
        default:
          sortBy = "createdAt";
          break;
      }
    }

    return sortBy;
  };

  const getSortByColumnId = (name) => {
    let sortBy;
    switch (name) {
      case "title":
        sortBy = 1;
        break;
      case "arabicTitle":
        sortBy = 1;
        break;
      case "description":
        sortBy = 2;
        break;
      case "arabicDescription":
        sortBy = 2;
        break;
      case "type":
        sortBy = 3;
        break;
      case "createdAt":
        sortBy = 4;
        break;
      default:
        sortBy = 4;
        break;
    }
    return sortBy;
  };

  const onSort = (column, sortDirection) => {
    const sortBy = getSortByColumnName(column.id);
    setRequest({
      ...request,
      sortBy: sortBy,
      sortDirection: sortDirection.toUpperCase(),
    });
    dispatch(
      getAds({
        ...request,
        sortDirection: sortDirection.toUpperCase(),
        sortBy: sortBy,
      })
    );
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(4);
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);

  const [adCopy, setAdCopy] = useState({});

  /**
   * Formik handling
   */
  const adForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: ad.title || "",
      arabicTitle: ad.arabicTitle || "",
      description: ad.description || "",
      arabicDescription: ad.arabicDescription || "",
      type: ad.type || "",
    },

    validationSchema: Yup.object({
      title: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.Title.Required")),
      arabicTitle: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.Title.Required")),
      description: Yup.string()
        .max(255, t("Validations.Common.Max", { length: 255 }))
        .required(t("Validations.Description.Required")),
      arabicDescription: Yup.string()
        .max(255, t("Validations.Common.Max", { length: 255 }))
        .required(t("Validations.ArabicDescription.Required")),
      type: Yup.string().required(t("Validations.Type.Required")),
    }),

    onSubmit: (values) => {
      setAdCopy(values);
      toggleTab(activeTab + 1, 33);
    },
  });

  const IsSubmitting = () =>
    addAdLoading || updateAdLoading || uploadingAdBanner;

  const [selectedAdId, setSelectedAdId] = useState(null);

  const onEdit = (id) => {
    setSelectedAdId(id);
    dispatch(getAd(id));
    toggleAdModal();
  };

  const onDelete = (id) => {
    setSelectedAdId(id);
    toggleAdDeleteModal();
  };

  const onDeleteConfirmation = () => {
    dispatch(deleteAd(selectedAdId));
    toggleAdDeleteModal();
    setTimeout(() => {
      dispatch(getAds());
      setSelectedAdId(null);
    }, 3000);
  };

  const onAdd = () => {
    setSelectedAdId(null);
    toggleAdModal();
  };

  const [passedSteps, setPassedSteps] = useState([1]);
  const [progressBarValue, setProgressBarValue] = useState(0);

  function toggleTab(tab, value) {
    if (activeTab !== tab) {
      var modifiedSteps = [...passedSteps, tab];
      if (tab >= 1 && tab <= 5) {
        setActiveTab(tab);
        setPassedSteps(modifiedSteps);
      }
    }
    setProgressBarValue(value);
  }

  const onChangeBanners = (changes) => {
    if (ad.id) {
      var images = Object.assign([], changes);
      images = [...images];
      setBanners(images);
    } else {
      setBanners(changes.map((img) => img.file));
    }
  };

  const onChangeArabicBanners = (changes) => {
    if (ad.id) {
      var images = Object.assign([], changes);
      images = [...images];
      setArabicBanners(images);
    } else {
      setArabicBanners(changes.map((img) => img.file));
    }
  };

  const [banners, setBanners] = useState([]);
  const [arabicBanners, setArabicBanners] = useState([]);

  const onSubmitAd = () => {
    if (ad.id) {
      banners.forEach((banner) => {
        if (banner.file) dispatch(uploadAdBanner(banner.file, "en"));
        else dispatch(uploadAdBanner(banner, "en"));
      });
      arabicBanners.forEach((arabicBanner) => {
        if (arabicBanner.file)
          dispatch(uploadAdBanner(arabicBanner.file, "ar"));
        else dispatch(uploadAdBanner(arabicBanner, "ar"));
      });
    } else {
      banners.forEach((banner) => {
        dispatch(uploadAdBanner(banner, "en"));
      });
      arabicBanners.forEach((arabicBanner) => {
        dispatch(uploadAdBanner(arabicBanner, "ar"));
      });
    }
  };

  useEffect(() => {
    if (uploadCount === 0) {
      if (ad.id) {
        dispatch(
          updateAd({
            ...adCopy,
            id: ad.id,
            bannerIds: ad.bannerIds,
            arabicBannerIds: ad.arabicBannerIds,
          })
        );
      } else {
        dispatch(
          addAd({
            ...adCopy,
            bannerIds: ad.bannerIds,
            arabicBannerIds: ad.arabicBannerIds,
          })
        );
      }
    }
  }, [uploadCount]);

  const [isExistingImagesSet, setIsExistingImagesSet] = useState(false);

  useEffect(() => {
    if (ad && ad.id && !isExistingImagesSet) {
      setIsExistingImagesSet(true);
      const banners = ad.banners.map((banner) => ({
        dataURL: banner.url,
        id: banner.id,
      }));
      const arabicBanners = ad.arabicBanners.map((arabicBanner) => ({
        dataURL: arabicBanner.url,
        id: arabicBanner.id,
      }));
      setBanners(banners);
      setArabicBanners(arabicBanners);
    }
  }, [ad]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Ads.Ads")} pageTitle={t("Common.Users")} />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="g-4 mb-3">
                    <Col className="col-sm-auto">
                      <div className="">
                        <div className="search-box ms-2">
                          <DebounceInput
                            className="form-control search"
                            placeholder={`${t("Common.Search")}...`}
                            minLength={2}
                            debounceTimeout={300}
                            onChange={(event) => onSearch(event.target.value)}
                            value={request.search}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </Col>
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <Button
                          color="success"
                          className="add-btn me-1"
                          id="create-btn"
                          onClick={onAdd}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          {t("Common.Add")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <DataTable
                    columns={columns}
                    data={rows}
                    progressPending={loadingAds}
                    progressComponent={
                      <Spinner
                        size="sm"
                        className="flex-shrink-0 m-5"
                        role="status"
                      ></Spinner>
                    }
                    pagination
                    paginationServer
                    sortServer
                    onSort={onSort}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    onChangePage={onChangePage}
                    defaultSortFieldId={defaultSortFieldId}
                    defaultSortAsc={defaultSortAsc}
                    persistTableHead
                    paginationTotalRows={totalRows}
                    paginationComponentOptions={{
                      rowsPerPageText: t("Pagination.Rows per page"),
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Add Modal */}
      <Modal
        isOpen={isAdModalOpen}
        toggle={() => {
          toggleAdModal();
        }}
        centered
      >
        <ModalHeader className="bg-light p-3">
          {selectedAdId ? t("Ads.Edit Ad") : t("Ads.Add Ad")}
          <Button
            type="button"
            onClick={() => {
              setIsAdModalOpen(false);
            }}
            className="btn-close"
            aria-label={t("Common.Close")}
          ></Button>
        </ModalHeader>
        <ModalBody>
          <CardBody className="form-steps">
            <div className="progress-nav mb-4">
              <Progress value={progressBarValue} style={{ height: "1px" }} />

              <Nav
                className="nav-pills progress-bar-tab custom-nav"
                role="tablist"
              >
                <NavItem>
                  <NavLink
                    disabled={IsSubmitting()}
                    to="#"
                    id="pills-gen-info-tab"
                    className={classnames(
                      {
                        active: activeTab === 1,
                        done: activeTab <= 5 && activeTab >= 0,
                      },
                      "rounded-pill"
                    )}
                    onClick={() => {
                      toggleTab(1, 0);
                    }}
                    tag="button"
                  >
                    {t("Counting.1")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    disabled={IsSubmitting()}
                    to="#"
                    id="pills-gen-info-tab"
                    className={classnames(
                      {
                        active: activeTab === 2,
                        done: activeTab <= 5 && activeTab > 1,
                      },
                      "rounded-pill"
                    )}
                    onClick={() => {
                      if (
                        adCopy &&
                        adCopy.title &&
                        adCopy.arabicTitle &&
                        adCopy.description &&
                        adCopy.arabicDescription
                      )
                        toggleTab(2, 33);
                      else return;
                    }}
                    tag="button"
                  >
                    {t("Counting.2")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    disabled={IsSubmitting()}
                    to="#"
                    id="pills-gen-info-tab"
                    className={classnames(
                      {
                        active: activeTab === 3,
                        done: activeTab <= 5 && activeTab > 2,
                      },
                      "rounded-pill"
                    )}
                    onClick={() => {
                      if (
                        adCopy &&
                        adCopy.title &&
                        adCopy.arabicTitle &&
                        adCopy.description &&
                        adCopy.arabicDescription &&
                        banners &&
                        banners.length > 0
                      )
                        toggleTab(3, 66);
                      return;
                    }}
                    tag="button"
                  >
                    {t("Counting.3")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    disabled={IsSubmitting()}
                    to="#"
                    id="pills-gen-info-tab"
                    className={classnames(
                      {
                        active: activeTab === 4,
                        done: activeTab <= 5 && activeTab > 3,
                      },
                      "rounded-pill"
                    )}
                    onClick={() => {
                      if (
                        adCopy &&
                        adCopy.title &&
                        adCopy.arabicTitle &&
                        adCopy.description &&
                        adCopy.arabicDescription &&
                        banners &&
                        banners.length > 0 &&
                        arabicBanners &&
                        arabicBanners.length > 0
                      )
                        toggleTab(4, 100);
                      else return;
                    }}
                    tag="button"
                  >
                    {t("Counting.4")}
                  </NavLink>
                </NavItem>
              </Nav>
            </div>

            <TabContent activeTab={activeTab}>
              <TabPane tabId={1}>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    adForm.handleSubmit();
                    return false;
                  }}
                  action="#"
                >
                  <div>
                    <div className="mb-4">
                      <div>
                        <h5 className="mb-1">
                          {t("Common.General Information")}
                        </h5>
                        <p className="text-muted">
                          {t("Common.Fill all Information as below")}
                        </p>
                      </div>
                    </div>
                    <Row>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="title" className="form-label">
                            {t("Fields.Title")}*
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            id="title"
                            placeholder={t("Fields.Enter Title")}
                            value={adForm.values.title || ""}
                            onChange={adForm.handleChange}
                            onBlur={adForm.handleBlur}
                            invalid={
                              adForm.touched.title && adForm.errors.title
                                ? true
                                : false
                            }
                          />
                          {adForm.touched.title && adForm.errors.title ? (
                            <FormFeedback type="invalid">
                              {adForm.errors.title}
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="arabicTitle" className="form-label">
                            {t("Fields.Arabic Title")}*
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            id="arabicTitle"
                            placeholder={t("Fields.Enter Arabic Title")}
                            value={adForm.values.arabicTitle || ""}
                            onChange={adForm.handleChange}
                            onBlur={adForm.handleBlur}
                            invalid={
                              adForm.touched.arabicTitle &&
                              adForm.errors.arabicTitle
                                ? true
                                : false
                            }
                          />
                          {adForm.touched.arabicTitle &&
                          adForm.errors.arabicTitle ? (
                            <FormFeedback type="invalid">
                              {adForm.errors.arabicTitle}
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                    </Row>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        {t("Fields.Type")}*
                      </Label>
                      <select
                        id="type"
                        className="form-select mb-3"
                        aria-label={t("Fields.Select Type")}
                        value={adForm.values.type}
                        onChange={adForm.handleChange}
                        onBlur={adForm.handleBlur}
                        invalid={
                          adForm.touched.type && adForm.errors.type
                            ? true
                            : false
                        }
                      >
                        <option id={null} value={null}>
                          {t("Fields.Select Type")}{" "}
                        </option>
                        {adTypes &&
                          adTypes.map(({ id, value, description }) => (
                            <option value={id} key={id}>
                              {i18n.language === "ar"
                                ? `${value.ar} | ${description.ar}`
                                : `${value.en} | ${description.en}`}
                            </option>
                          ))}
                      </select>
                      {adForm.touched.type && adForm.errors.type ? (
                        <FormFeedback type="invalid">
                          {adForm.errors.type}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="description" className="form-label">
                        {t("Fields.Description")}*
                      </Label>
                      <Input
                        type="textarea"
                        row="2"
                        className="form-control"
                        id="description"
                        placeholder={t("Fields.Enter Description")}
                        value={adForm.values.description || ""}
                        onChange={adForm.handleChange}
                        onBlur={adForm.handleBlur}
                        invalid={
                          adForm.touched.description &&
                          adForm.errors.description
                            ? true
                            : false
                        }
                      />
                      {adForm.touched.description &&
                      adForm.errors.description ? (
                        <FormFeedback type="invalid">
                          {adForm.errors.description}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="arabicDescription" className="form-label">
                        {t("Fields.Arabic Description")}*
                      </Label>
                      <Input
                        type="textarea"
                        row="2"
                        className="form-control"
                        id="arabicDescription"
                        placeholder={t("Fields.Enter Arabic Description")}
                        value={adForm.values.arabicDescription || ""}
                        onChange={adForm.handleChange}
                        onBlur={adForm.handleBlur}
                        invalid={
                          adForm.touched.arabicDescription &&
                          adForm.errors.arabicDescription
                            ? true
                            : false
                        }
                      />
                      {adForm.touched.arabicDescription &&
                      adForm.errors.arabicDescription ? (
                        <FormFeedback type="invalid">
                          {adForm.errors.arabicDescription}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-3 mt-4">
                    <button
                      type="submit"
                      className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    >
                      {i18n.language === "ar" ? (
                        <i className="ri-arrow-left-line label-icon align-middle fs-16 ms-2"></i>
                      ) : (
                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                      )}
                      {t("Common.Go to more info")}
                    </button>
                  </div>
                </Form>
              </TabPane>

              <TabPane tabId={2}>
                <div>
                  <div className="mb-4">
                    <div>
                      <h5 className="mb-1">{t("Fields.English Banners")}</h5>
                      <p className="text-muted">
                        {t(
                          "Validations.EnglishBanners.Upload maximum english images",
                          {
                            count: t("Counting.6"),
                          }
                        )}
                        <br />
                        {t(
                          "Messages.Information.Supported file types are jpg, png and gif"
                        )}
                        <br />
                        {ad.type === "Customer_Home"
                          ? t(
                              "Messages.Information.Recommended image resolution",
                              { resolution: " 580x425" }
                            )
                          : t(
                              "Messages.Information.Recommended image resolution",
                              { resolution: " 592x538" }
                            )}
                      </p>
                    </div>
                  </div>
                  <div className="mb-5">
                    <ImageUploader
                      isEdit={ad.id ? true : false}
                      images={banners}
                      maxNumber={6}
                      onChange={onChangeBanners}
                    />
                  </div>
                </div>
                <div className="d-flex align-items-start gap-3 mt-4">
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none btn-label previestab"
                    onClick={() => {
                      toggleTab(activeTab - 1, 33);
                    }}
                  >
                    {i18n.language === "ar" ? (
                      <i className="ri-arrow-right-line label-icon align-middle fs-16 me-2"></i>
                    ) : (
                      <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2"></i>
                    )}{" "}
                    {t("Common.Back to General")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    onClick={() => {
                      if (banners && banners.length === 0) return;
                      toggleTab(activeTab + 1, 66);
                    }}
                  >
                    {i18n.language === "ar" ? (
                      <i className="ri-arrow-left-line label-icon align-middle fs-16 ms-2"></i>
                    ) : (
                      <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                    )}
                    {t("Common.Next")}
                  </button>
                </div>
              </TabPane>
              <TabPane tabId={3}>
                <div>
                  <div className="mb-4">
                    <div>
                      <h5 className="mb-1">{t("Fields.Arabic Banners")}</h5>
                      <p className="text-muted">
                        {t(
                          "Validations.ArabicBanners.Upload maximum arabic images",
                          {
                            count: t("Counting.6"),
                          }
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="mb-5">
                    <ImageUploader
                      isEdit={ad.id ? true : false}
                      images={arabicBanners}
                      maxNumber={6}
                      onChange={onChangeArabicBanners}
                    />
                  </div>
                </div>
                <div className="d-flex align-items-start gap-3 mt-4">
                  <button
                    disabled={IsSubmitting()}
                    type="button"
                    className="btn btn-link text-decoration-none btn-label previestab"
                    onClick={() => {
                      toggleTab(activeTab - 1, 66);
                    }}
                  >
                    {i18n.language === "ar" ? (
                      <i className="ri-arrow-right-line label-icon align-middle fs-16 me-2"></i>
                    ) : (
                      <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2"></i>
                    )}{" "}
                    {t("Common.Back to English Banners")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    onClick={() => {
                      if (!arabicBanners || arabicBanners.length === 0) return;
                      toggleTab(activeTab + 1, 100);
                      onSubmitAd();
                    }}
                  >
                    {i18n.language === "ar" ? (
                      <i className="ri-arrow-left-line label-icon align-middle fs-16 ms-2"></i>
                    ) : (
                      <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                    )}
                    {t("Common.Submit")}
                  </button>
                </div>
              </TabPane>
              <TabPane tabId={4}>
                <div className="text-center">
                  {addAdLoading ||
                    updateAdLoading ||
                    (uploadCount && uploadCount > 0 && (
                      <Spinner
                        size="sm"
                        className="flex-shrink-0 m-5"
                        role="status"
                      ></Spinner>
                    ))}
                </div>
              </TabPane>
            </TabContent>
          </CardBody>
        </ModalBody>
      </Modal>

      {/* Remove Modal */}
      <Modal
        isOpen={isAdDeleteModalOpen}
        toggle={() => {
          toggleAdDeleteModal();
        }}
        centered
      >
        <div className="modal-header">
          <Button
            type="button"
            onClick={() => setIsAdDeleteModalOpen(false)}
            className="btn-close"
            aria-label="Close"
          >
            {" "}
          </Button>
        </div>
        <ModalBody>
          <div className="mt-2 text-center">
            <lord-icon
              src="https://cdn.lordicon.com/gsqxdxog.json"
              trigger="loop"
              colors="primary:#f7b84b,secondary:#f06548"
              style={{ width: "100px", height: "100px" }}
            ></lord-icon>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("Common.Are you Sure?")}</h4>
              <p className="text-muted mx-4 mb-0">
                {t("Common.Are you Sure You want to Remove this Record?")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              onClick={() => {
                setSelectedAdId(null);
                toggleAdDeleteModal();
              }}
            >
              {t("Common.Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger "
              id="delete-record"
              onClick={() => onDeleteConfirmation()}
            >
              {t("Common.Yes, Delete It!")}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default AdsPage;
